import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Info, CheckCircle2, AlertTriangle, AlertCircle, X } from "lucide-react";

/**
 * Toasts globaux stylés DS Bold Duotone.
 *
 * Reçoit les notifications via l'event 'ds-toast' sur window. C'est cet
 * event qui est émis par le monkey-patch de Store.addNotification fait
 * dans App.js — les 26 call sites existants continuent d'utiliser
 * `Store.addNotification(...)` de react-notifications-component sans
 * modification, mais leurs notifications sortent ici.
 */

const VARIANTS = {
  info:    { cls: "bg-info-bg text-info border-info/30",       Icon: Info },
  success: { cls: "bg-success-bg text-success border-success/30", Icon: CheckCircle2 },
  warn:    { cls: "bg-warn-bg text-warn border-warn/30",       Icon: AlertTriangle },
  danger:  { cls: "bg-danger-bg text-danger border-danger/30", Icon: AlertCircle },
  neutral: { cls: "bg-ink text-cream border-ink-3",            Icon: Info },
};

// react-notifications-component type → DS variant
const TYPE_MAP = {
  success: "success",
  danger:  "danger",
  warning: "warn",
  info:    "info",
  default: "neutral",
};

const POSITIONS = {
  "top-right":     "top-4 right-4",
  "top-left":      "top-4 left-4",
  "top-center":    "top-4 left-1/2 -translate-x-1/2",
  "bottom-right":  "bottom-4 right-4",
  "bottom-left":   "bottom-4 left-4",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2",
  "center-center": "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
};

let _id = 0;

function ToastItem({ item, onClose }) {
  const variant = VARIANTS[item.variant] || VARIANTS.neutral;
  const Icon = variant.Icon;
  return (
    <div
      role="status"
      className={`flex items-start gap-3 border rounded-lg p-3 shadow-lg animate-slide-up ${variant.cls}`}
    >
      <Icon className="w-[18px] h-[18px] shrink-0 mt-0.5" strokeWidth={1.8} />
      <div className="flex-1 leading-tight">
        {item.title && <div className="text-sm font-semibold">{item.title}</div>}
        {item.message && (
          <div className={`text-sm leading-relaxed ${item.title ? "mt-0.5 opacity-90" : ""}`}>
            {item.message}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="shrink-0 -mr-1 -mt-1 p-1 rounded hover:bg-text/10"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

function Stack({ position, items, onDismiss }) {
  const posCls = POSITIONS[position] || POSITIONS["top-right"];
  if (items.length === 0) return null;
  return (
    <div
      data-ds-theme="default"
      className={`fixed z-toast flex flex-col gap-2 max-w-[360px] w-[calc(100%-2rem)] ${posCls}`}
      aria-live="polite"
      aria-atomic="false"
    >
      {items.map((t) => (
        <ToastItem key={t.id} item={t} onClose={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

export default function ToastHost() {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems((arr) => arr.filter((x) => x.id !== id));
  }, []);

  useEffect(() => {
    const onAdd = (e) => {
      const opts = e.detail || {};
      const id = ++_id;
      const variant = TYPE_MAP[opts.type] || "neutral";
      const duration = opts.dismiss?.duration ?? 4000;
      const position = `${opts.insert || "top"}-${(opts.container || "top-right").split("-")[1] || "right"}`;
      const item = {
        id,
        variant,
        title: opts.title,
        message: opts.message,
        position,
      };
      setItems((arr) => [...arr, item]);
      if (duration > 0) {
        setTimeout(() => {
          setItems((arr) => arr.filter((x) => x.id !== id));
        }, duration);
      }
    };
    window.addEventListener("ds-toast", onAdd);
    return () => window.removeEventListener("ds-toast", onAdd);
  }, []);

  if (typeof document === "undefined") return null;

  // Grouper par position pour avoir des stacks distincts
  const byPos = items.reduce((acc, it) => {
    (acc[it.position] = acc[it.position] || []).push(it);
    return acc;
  }, {});

  return createPortal(
    <>
      {Object.entries(byPos).map(([position, list]) => (
        <Stack key={position} position={position} items={list} onDismiss={dismiss} />
      ))}
    </>,
    document.body
  );
}
